/*
  Выбор периода счётчиков окупаемости — §3.11.3.

  08.09.2026, решение владельца: период выбирается календарём, а сам период
  показывается в поле выбора - подпись «с 01.01.2026 по 08.09.2026» под
  заголовком убрана. Одна и та же вещь не должна быть написана в двух местах:
  разъедутся - человек поверит той, что заметнее.

  Устройство панели: сначала готовые периоды (их выбирают в девяти случаях из
  десяти), под ними - две даты календарём для всего остального. Кнопка
  «Применить» есть именно у дат: пока человек правит вторую дату, первая уже
  изменилась, и запрашивать отчёт на каждое нажатие клавиши значило бы считать
  портфель по три раза на одно действие.

  **Даты передаются как есть, а не как название периода.** «Последний квартал»
  через год означает другой отрезок, и повторить прошлую цифру было бы нечем;
  сервер получает конкретные `since` и `until` (§8.4, И2).
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { PERIODS, date, periodStart, today } from '../api/format.js'

export default function PeriodPicker({ since, until, historyFrom, onChange }) {
  const [open, setOpen] = useState(false)
  const [draftSince, setDraftSince] = useState(since)
  const [draftUntil, setDraftUntil] = useState(until)
  const root = useRef(null)

  // Черновик дат следует за выбранным периодом: открыв панель после «за
  // квартал», человек должен увидеть в календарях именно квартал, а не то, что
  // набирал в прошлый раз.
  useEffect(() => {
    setDraftSince(since)
    setDraftUntil(until)
  }, [since, until])

  useEffect(() => {
    if (!open) return undefined
    const outside = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false)
    }
    const escape = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', outside)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', outside)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const choosePreset = useCallback(
    (key) => {
      const start = periodStart(key, { historyFrom })
      if (!start) return
      onChange(start, today())
      setOpen(false)
    },
    [onChange, historyFrom],
  )

  const apply = useCallback(() => {
    onChange(draftSince, draftUntil)
    setOpen(false)
  }, [draftSince, draftUntil, onChange])

  // Период за границами возможного не запрашивается: конец раньше начала даёт
  // пустой отчёт, который человек прочитает как «ничего не сделано».
  const applicable = Boolean(draftSince) && Boolean(draftUntil) && draftSince <= draftUntil

  return (
    <span className="period" ref={root}>
      <button
        type="button"
        className="period__button no-hover-lift"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="period__label">Период</span>
        <span className="period__value">
          {date(since)} - {date(until)}
        </span>
      </button>

      {open ? (
        <div className="period__panel" role="dialog" aria-label="Выбор периода">
          <div className="period__presets">
            {Object.entries(PERIODS).map(([key, title]) =>
              key === 'all' && !historyFrom ? null : (
                <button
                  key={key}
                  type="button"
                  className="period__preset no-hover-lift"
                  onClick={() => choosePreset(key)}
                >
                  {title}
                </button>
              ),
            )}
          </div>

          <div className="period__dates">
            <label className="field">
              <span className="field__label">С какого дня</span>
              <input
                className="field__input"
                type="date"
                value={draftSince ?? ''}
                max={draftUntil ?? undefined}
                onChange={(event) => setDraftSince(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">По какой день</span>
              <input
                className="field__input"
                type="date"
                value={draftUntil ?? ''}
                min={draftSince ?? undefined}
                max={today()}
                onChange={(event) => setDraftUntil(event.target.value)}
              />
            </label>
          </div>

          <button
            type="button"
            className="button"
            onClick={apply}
            disabled={!applicable}
            title={applicable ? undefined : 'Конец периода не может быть раньше начала'}
          >
            Применить
          </button>
        </div>
      ) : null}
    </span>
  )
}
